import { type AccountInfo, InteractionStatus, PublicClientApplication } from '@azure/msal-browser';
import { Ref } from 'vue';

export type MsalContext = {
  instance: PublicClientApplication;
  accounts: Ref<AccountInfo[]>;
  inProgress: Ref<InteractionStatus>;
  signout: CallableFunction;
};

export function useMsal(): MsalContext {
  const internalInstance = useNuxtApp();

  if (!internalInstance) {
    throw new Error('useMsal() cannot be called outside the setup() function of a component');
  }

  const { instance, accounts, inProgress } = toRefs(internalInstance.$msal);

  if (!instance.value || !accounts.value || !inProgress.value) {
    throw new Error('Please install the msalPlugin');
  }

  if (inProgress.value === InteractionStatus.Startup) {
    instance.value.handleRedirectPromise().catch(() => {
      // Errors should be handled by listening to the LOGIN_FAILURE event
      return;
    });
  }

  function signout() {
    const account = instance.value.getActiveAccount() ?? accounts.value[0];
    if (!account) return;

    instance.value.logoutRedirect({
      account,
      idTokenHint: sessionStorage.getItem('idToken') ?? undefined,
    });
  }

  return {
    instance: instance.value,
    accounts,
    inProgress,
    signout,
  };
}
